import mapLabel from '@assets/map_label.png';
import { MapComponent } from '@components/shared/Maps/MapComponent';
import { MapSkeleton } from '@components/shared/Maps/MapSkeleton';
import { useMapsStore } from '@components/shared/Maps/store';
import { getViewportFromLatLng, PlaceSearchResult } from '@components/shared/Maps/utils';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { MapHandler } from './mapHandler';
import { PlacesAutocomplete } from './PlacesAutocomplete';
import { useGeolocated } from './useGeoLocated';

// Props Interface
interface TaMapsProps {
	onLocationSelect?: (address: string, lat: number, lng: number) => void;
	currentAddress?: string;
	marker?: google.maps.LatLngLiteral;
	onSave?: () => void;
	disabled?: boolean;
}

export function TaMaps({
	onLocationSelect,
	currentAddress,
	marker,
	onSave,
	disabled = false,
}: TaMapsProps) {
	// Geolocation Hook
	const { isGeolocationEnabled, coords, isLoading } = useGeolocated();
	// State Management
	const { setAddress } = useMapsStore();
	const [selectedPlace, setSelectedPlace] = useState<any>(null);

	// Initial Position Setup
	const initialPosition = useMemo(
		() =>
			coords
				? {
						lat: coords.latitude,
						lng: coords.longitude,
					}
				: null,
		[coords]
	);

	const [mapInitialPosition, setMapInitialPosition] = useState<google.maps.LatLngLiteral | null>(
		initialPosition
	);

	// Effects
	useEffect(() => {
		if (marker) {
			setMapInitialPosition(marker);
		} else if (coords) {
			setMapInitialPosition({
				lat: coords.latitude,
				lng: coords.longitude,
			});
		}
	}, [marker, coords]);

	useEffect(() => {
		if (currentAddress) {
			setAddress(currentAddress);
		}
	}, [currentAddress, setAddress]);

	// Event Handlers
	const handleMapClick = useCallback(
		async (position: google.maps.LatLngLiteral, address: string) => {
			setAddress(address);
			setMapInitialPosition(position);

			if (onLocationSelect) {
				onLocationSelect(address, position.lat, position.lng);
			}

			const newViewport = await getViewportFromLatLng(position);

			setSelectedPlace({ viewport: newViewport });
		},
		[setAddress, onLocationSelect]
	);

	const handlePlaceSelect = (place: PlaceSearchResult | null) => {
		if (place) {
			setMapInitialPosition({
				lat: place.position.lat,
				lng: place.position.lng,
			});

			setSelectedPlace(place.geometry);
			setAddress(place.address);
			if (onLocationSelect && place.geometry?.location) {
				onLocationSelect(place.address, place.geometry.location.lat, place.geometry.location.lng);
			}
		}
	};

	// Loading State
	if (isLoading) {
		return <MapSkeleton />;
	}

	// Render Conditions
	const hasInitialPosition = mapInitialPosition && mapInitialPosition.lat && mapInitialPosition.lng;
	const showMap = hasInitialPosition || isGeolocationEnabled;

	// Component Render
	return (
		<>
			<div
				style={{
					width: '100%',
					height: '100%',
					...(!showMap && {
						backgroundImage: `url(${mapLabel})`,
						backgroundRepeat: 'no-repeat',
						backgroundPosition: 'center',
						backgroundSize: 'cover',
					}),
				}}
			>
				<PlacesAutocomplete disabled={disabled} onPlaceSelect={handlePlaceSelect} onSave={onSave} />

				{showMap && (
					<MapComponent
						disabled={disabled}
						initialCenter={mapInitialPosition as any}
						onMapClick={handleMapClick}
						position={mapInitialPosition as any}
					/>
				)}

				{showMap && (
					<MapHandler
						place={{
							viewport: selectedPlace?.viewport,
						}}
					/>
				)}
			</div>
		</>
	);
}
